import React from 'react';
import { IHTCalculation } from '../../types';
import { Gift, Check } from 'lucide-react';
import { RegulationReference } from './RegulationReference';
import { getAuthority, FeatureArea } from '../../server/rules/authorities';
import { selectReportPanels } from './selectReportPanels';

interface Props {
 ihtCalc?: IHTCalculation | null;
}

const fmt = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

const formatYears = (y: number): string => {
 const whole = Math.floor(y);
 const months = Math.round((y - whole) * 12);
 if (months === 0) return `${whole} year${whole !== 1 ? 's' : ''}`;
 return `${whole}y ${months}m`;
};

const taperBandLabel = (years: number): string => {
 if (years < 3) return '0–3 years (100%)';
 if (years < 4) return '3–4 years (80%)';
 if (years < 5) return '4–5 years (60%)';
 if (years < 6) return '5–6 years (40%)';
 return '6–7 years (20%)';
};

export const GiftTaperBreakdown: React.FC<Props> = ({ ihtCalc }) => {
 // showTaper = showFigures && (gift_taper_details present). For supported and
 // pre-2017 deaths this is exactly the old `details?.length` guard; for pre-IHT
 // (figures suppressed) it forces this panel off — no taper table for a date we
 // cannot compute.
 const panels = selectReportPanels(ihtCalc);
 if (!panels.showTaper) return null;
 const details = ihtCalc!.breakdown!.gift_taper_details!;

 const totalTaperRelief = ihtCalc?.breakdown?.taper_relief_total ?? 0;
 const hasAnyTax = details.some(d => d.taxBeforeTaper > 0);
 const allCoveredByNrb = !hasAnyTax;
 const totalChargeableGifts = ihtCalc?.breakdown?.gifts_chargeable_total ?? 0;
 const nrbAvailable = (ihtCalc?.breakdown?.nrb_available ?? 0) + totalChargeableGifts; // NRB before gift reduction
 const showAttributionCol = details.some(d => d.fullAmountBeforeAttribution != null && d.fullAmountBeforeAttribution !== d.originalAmount);
 const colCount = showAttributionCol ? 11 : 10;

 // Sources cited as in force at the date of death (gifts/taper/AE all predate 2017,
 // so these resolve for every supported death; the labels stay descriptive).
 const dod = ihtCalc?.breakdown?.date_of_death ?? null;
 const giftSources = ([
   ['TNRB', 'Lifetime gifts use the Nil Rate Band before the estate is taxed'],
   ['TAPER_RELIEF', 'Taper relief rules'],
   ['ANNUAL_EXEMPTION', 'Annual exemption rules'],
 ] as [FeatureArea, string][])
   .map(([area, title]) => ({ title, a: getAuthority(area, dod) }))
   .filter(x => x.a !== null)
   .map(x => ({ title: x.title, legislationReference: x.a!.legislation, hmrcManualReference: x.a!.manual }));

 return (
 <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
 <div className="bg-[#1A1A1A] px-6 py-4">
 <div className="flex items-center gap-2">
 <Gift className="w-4 h-4 text-[#3730A3]" />
 <h2 className="text-xl font-bold text-white">Lifetime Gift Tax Analysis</h2>
 </div>
 <p className="text-[#AFA99F] text-sm mt-1">
 {details.length} chargeable gift{details.length !== 1 ? 's' : ''} within 7 years of death.
 {totalTaperRelief > 0 && ` Taper relief saves ${fmt(Math.round(totalTaperRelief))}.`}
 {allCoveredByNrb && ' All gifts covered by the Nil Rate Band.'}
 </p>
 </div>

 <div className="p-6">
 {/* No-tax summary when all gifts covered by NRB */}
 {allCoveredByNrb && (
 <div className="bg-white border border-[#E8E4DB] rounded-lg p-3 mb-4">
 <div className="flex items-start gap-2">
 <Check className="w-4 h-4 text-[#3730A3] mt-0.5 shrink-0" />
 <div>
 <p className="text-sm font-bold text-[#1A1A1A]">No gift tax arises</p>
 <p className="text-xs text-[#1A1A1A] mt-1 leading-relaxed">
 Lifetime gifts used {fmt(totalChargeableGifts)} of the available {fmt(nrbAvailable)} Nil Rate Band.
 No gift tax arises because the {nrbAvailable > 325000 ? 'combined ' : ''}NRB covers the gifts.
 </p>
 <p className="text-xs text-[#3730A3] mt-1 leading-relaxed">
 The remaining {fmt(nrbAvailable - totalChargeableGifts)} NRB is applied to the estate.
 </p>
 </div>
 </div>
 </div>
 )}

 {/* Per-gift table */}
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-[#E8E4DB]">
 <th className="text-left py-2 pr-3 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">Gift date</th>
 <th className="text-right py-2 px-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">Amount</th>
 {showAttributionCol && (
 <th className="text-right py-2 px-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">Attributed to deceased</th>
 )}
 <th className="text-right py-2 px-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">Chargeable</th>
 <th className="text-right py-2 px-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">Years before death</th>
 <th className="text-right py-2 px-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">NRB remaining</th>
 <th className="text-right py-2 px-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">NRB used</th>
 <th className="text-right py-2 px-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">Taxable above NRB</th>
 <th className="text-right py-2 px-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">Tax before taper</th>
 <th className="text-right py-2 px-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">Taper relief</th>
 <th className="text-right py-2 pl-2 font-bold text-[#6B6762] uppercase tracking-wide text-[10px]">Tax after taper</th>
 </tr>
 </thead>
 <tbody>
 {details.map((gift, idx) => {
 const hasAttribution = gift.fullAmountBeforeAttribution != null && gift.fullAmountBeforeAttribution !== gift.originalAmount;
 const isTaxable = gift.taxBeforeTaper > 0;
 const couldHaveTaper = gift.yearsBefore >= 3;
 return (
 <React.Fragment key={idx}>
 <tr className={`border-b border-[#E8E4DB] ${isTaxable ? '' : 'text-[#AFA99F]'}`}>
 <td className="py-2 pr-3 font-medium text-[#3F3D39]">{gift.date}</td>
 <td className="py-2 px-2 text-right font-mono">
 {fmt(hasAttribution ? gift.fullAmountBeforeAttribution! : gift.originalAmount)}
 {gift.exemptAmount > 0 && (
 <span className="block text-[10px] text-[#3730A3]">
 -{fmt(gift.exemptAmount)} exempt
 {gift.exemptionDetails && gift.exemptionDetails.length > 0 && (
 <span className="block text-[#3730A3]">
 {gift.exemptionDetails.map((ed, ei) => (
 <span key={ei} className="block">
 {fmt(ed.amount)} {ed.isCarryForward ? `c/f from ${ed.taxYear}` : `AE ${ed.taxYear}`}
 </span>
 ))}
 </span>
 )}
 </span>
 )}
 </td>
 {showAttributionCol && (
 <td className="py-2 px-2 text-right font-mono">
 {fmt(gift.originalAmount)}
 {hasAttribution && (
 <span className="block text-[10px] text-[#AFA99F]">
 {Math.round((gift.originalAmount / gift.fullAmountBeforeAttribution!) * 100)}%
 </span>
 )}
 </td>
 )}
 <td className="py-2 px-2 text-right font-mono">{fmt(gift.chargeableAmount)}</td>
 <td className="py-2 px-2 text-right">
 <span className="font-mono">{formatYears(gift.yearsBefore)}</span>
 <span className="block text-[10px] text-[#AFA99F]">{taperBandLabel(gift.yearsBefore)}</span>
 </td>
 <td className="py-2 px-2 text-right font-mono">{fmt(gift.nrbRemainingBefore)}</td>
 <td className="py-2 px-2 text-right font-mono">
 {gift.nrbUsed > 0 ? fmt(gift.nrbUsed) : '—'}
 </td>
 <td className="py-2 px-2 text-right font-mono">
 {gift.taxableAfterNrb > 0 ? fmt(gift.taxableAfterNrb) : '—'}
 </td>
 <td className="py-2 px-2 text-right font-mono">
 {isTaxable ? fmt(Math.round(gift.taxBeforeTaper)) : '—'}
 </td>
 <td className="py-2 px-2 text-right font-mono text-[#3730A3]">
 {gift.taperRelief > 0 ? fmt(Math.round(gift.taperRelief)) : '—'}
 </td>
 <td className="py-2 pl-2 text-right font-mono font-bold">
 {isTaxable ? fmt(Math.round(gift.taxAfterTaper)) : 'Nil'}
 </td>
 </tr>
 {/* Taper review note for non-taxable gifts in taper range */}
 {!isTaxable && couldHaveTaper && (
 <tr className="border-b border-[#E8E4DB]">
 <td colSpan={colCount} className="py-1 pl-6">
 <span className="text-[10px] text-[#AFA99F] italic flex items-center gap-1">
 <Check className="w-3 h-3 text-[#AFA99F] shrink-0" />
 Taper relief reviewed: not applicable, because no tax arises on this gift.
 </span>
 </td>
 </tr>
 )}
 </React.Fragment>
 );
 })}
 </tbody>
 {/* Totals row */}
 <tfoot>
 <tr className="border-t-2 border-[#E8E4DB] bg-[#FAFAF8] font-bold">
 <td className="py-2 pr-3 text-[#3F3D39]" colSpan={showAttributionCol ? 3 : 2}>Total</td>
 <td className="py-2 px-2 text-right font-mono text-[#3F3D39]">{fmt(totalChargeableGifts)}</td>
 <td className="py-2 px-2" colSpan={2}></td>
 <td className="py-2 px-2 text-right font-mono text-[#3F3D39]">
 {fmt(details.reduce((s, d) => s + d.nrbUsed, 0))}
 </td>
 <td className="py-2 px-2 text-right font-mono text-[#3F3D39]">
 {hasAnyTax ? fmt(details.reduce((s, d) => s + d.taxableAfterNrb, 0)) : '—'}
 </td>
 <td className="py-2 px-2 text-right font-mono text-[#3F3D39]">
 {hasAnyTax ? fmt(Math.round(details.reduce((s, d) => s + d.taxBeforeTaper, 0))) : '—'}
 </td>
 <td className="py-2 px-2 text-right font-mono text-[#3730A3]">
 {totalTaperRelief > 0 ? fmt(Math.round(totalTaperRelief)) : '—'}
 </td>
 <td className="py-2 pl-2 text-right font-mono font-bold text-[#1A1A1A]">
 {hasAnyTax ? fmt(Math.round(details.reduce((s, d) => s + d.taxAfterTaper, 0))) : 'Nil'}
 </td>
 </tr>
 </tfoot>
 </table>
 </div>

 {/* Per-year AE audit trail */}
 {ihtCalc?.breakdown?.gifts_ae_by_year && ihtCalc.breakdown.gifts_ae_by_year.length > 0 && (
 <div className="mt-4 pt-3 border-t border-[#E8E4DB]">
 <p className="text-[10px] font-bold text-[#6B6762] uppercase tracking-wide mb-2">Annual Exemption by Tax Year</p>
 <table className="w-full text-[10px]">
 <thead>
 <tr className="border-b border-[#E8E4DB]">
 <th className="text-left py-1 pr-2 font-bold text-[#6B6762] uppercase tracking-wide">Tax year</th>
 <th className="text-right py-1 px-1 font-bold text-[#6B6762] uppercase tracking-wide">Own AE</th>
 <th className="text-right py-1 px-1 font-bold text-[#6B6762] uppercase tracking-wide">Used</th>
 <th className="text-right py-1 px-1 font-bold text-[#6B6762] uppercase tracking-wide">Unused</th>
 <th className="text-right py-1 px-1 font-bold text-[#6B6762] uppercase tracking-wide">c/f in</th>
 <th className="text-right py-1 px-1 font-bold text-[#6B6762] uppercase tracking-wide">c/f used</th>
 <th className="text-right py-1 pl-1 font-bold text-[#6B6762] uppercase tracking-wide">Total applied</th>
 </tr>
 </thead>
 <tbody>
 {ihtCalc.breakdown.gifts_ae_by_year.map((y, i) => (
 <tr key={i} className="border-b border-[#E8E4DB]">
 <td className="py-1 pr-2 font-medium text-[#3F3D39]">{y.taxYear}</td>
 <td className="py-1 px-1 text-right font-mono text-[#6B6762]">{fmt(y.ownAE)}</td>
 <td className="py-1 px-1 text-right font-mono text-[#3F3D39]">{y.ownUsed > 0 ? fmt(y.ownUsed) : '—'}</td>
 <td className="py-1 px-1 text-right font-mono text-[#AFA99F]">{fmt(y.ownRemaining)}</td>
 <td className="py-1 px-1 text-right font-mono text-[#3730A3]">{y.carryForwardIn > 0 ? fmt(y.carryForwardIn) : '—'}</td>
 <td className="py-1 px-1 text-right font-mono text-[#3730A3]">{y.carryForwardUsed > 0 ? fmt(y.carryForwardUsed) : '—'}</td>
 <td className="py-1 pl-1 text-right font-mono font-bold text-[#3F3D39]">{y.totalExemptionApplied > 0 ? fmt(y.totalExemptionApplied) : '—'}</td>
 </tr>
 ))}
 </tbody>
 <tfoot>
 <tr className="border-t-2 border-[#E8E4DB] bg-[#FAFAF8] font-bold">
 <td className="py-1 pr-2 text-[#3F3D39]">Total</td>
 <td className="py-1 px-1" colSpan={5}></td>
 <td className="py-1 pl-1 text-right font-mono text-[#3F3D39]">
 {fmt(ihtCalc.breakdown.gifts_ae_by_year.reduce((s, y) => s + y.totalExemptionApplied, 0))}
 </td>
 </tr>
 </tfoot>
 </table>
 </div>
 )}

 {/* Explanation */}
 <div className="mt-4 pt-3 border-t border-[#E8E4DB] space-y-2">
 <div className="text-[10px] text-[#6B6762] leading-relaxed">
 <p className="mb-1">
 <strong>How this works:</strong> Gifts made within 7 years of death use up the Nil Rate Band in chronological order (oldest first).
 Only the portion of gifts exceeding the available NRB is taxable at 40%.
 </p>
 <p>
 <strong>Taper relief</strong> reduces the <em>tax</em> (not the gift value) on gifts made more than 3 years before death.
 The relief is graduated: 20% reduction at 3–4 years, increasing to 80% at 6–7 years.
 </p>
 <p className="mt-1">
 <strong>Annual exemption (AE):</strong> Each tax year has a £3,000 annual exemption. Unused annual exemption can only be carried forward for one tax year.
 Current year's exemption is applied first; any remainder uses the prior year's carry-forward (c/f).
 </p>
 </div>

 <RegulationReference
 contextLine="These explanations follow HMRC guidance on inheritance tax calculations."
 asAtDate={dod}
 sources={giftSources}
 />
 </div>
 </div>
 </div>
 );
};
